import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useContext } from 'react';
import { LabeledContext } from '@sixui/ui';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IFileUploaderProps } from './FileUploader';
import { getFormErrorText } from '~/helpers/getFormErrorText';
import { FileUploader } from './FileUploader';

export type IFormFileUploaderProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    Omit<IFileUploaderProps, 'sx' | 'defaultValue' | 'value'> & {
      readOnly?: boolean;
      required?: boolean;
      hasError?: boolean;
      errorText?: string;
    };

// TODO: use FormField
export const FormFileUploader = <TFieldValues extends FieldValues>(
  props: IFormFileUploaderProps<TFieldValues>,
): React.ReactNode => {
  const {
    name,
    shouldUnregister,
    defaultValue,
    control,
    rules: rulesProp,
    disabled: disabledProp,
    readOnly: readOnlyProp,
    required: requiredProp,
    errorText: errorTextProp,
    ...other
  } = props;

  const labeledContext = useContext(LabeledContext);
  const readOnly = readOnlyProp || labeledContext?.readOnly;
  const required = requiredProp || labeledContext?.required;
  const disabled = disabledProp || labeledContext?.disabled || readOnly;

  const rules = {
    ...rulesProp,
    required: rulesProp?.required || required,
  };

  const { t } = useTranslation();
  const { field, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const { error: validationError } = fieldState;
  const validationErrorText = validationError
    ? getFormErrorText(validationError, rules, t)
    : undefined;

  const errorText =
    errorTextProp ?? labeledContext?.errorText ?? validationErrorText;

  return <FileUploader {...other} {...field} errorText={errorText} />;
};
